import { useState } from 'react';
import type { UserDelegation, UserDelegationStatus } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { CalendarPlus, Loader2, Pencil } from 'lucide-react';
import { OutOfOfficeDialog } from './OutOfOfficeDialog';
import { useOutOfOffice } from './useOutOfOffice';

const STATUS: Record<UserDelegationStatus, { label: string; className: string }> = {
  active: { label: 'On now', className: 'bg-green-100 text-green-800 border-green-200' },
  scheduled: { label: 'Planned', className: 'bg-blue-100 text-blue-800 border-blue-200' },
  ended: { label: 'Ended', className: 'bg-gray-100 text-gray-700 border-gray-200' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-500 border-gray-200' },
};

interface StatusBadgeProps {
  status: UserDelegationStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const { label, className } = STATUS[status];
  return <Badge variant="outline" className={className}>{label}</Badge>;
}

/**
 * CR-5889: My Account → Out of Office. The user turns out of office on or
 * off, plans time away with the colleague who covers, and sees all their
 * time away plus the colleagues they are covering for. No admin is involved.
 */
export function OutOfOfficeTab() {
  const { user } = useAuth();
  const { formatDate } = useFormatters();
  const ooo = useOutOfOffice(user?.id);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<UserDelegation | null>(null);
  const [ending, setEnding] = useState<UserDelegation | null>(null);
  const [endProblem, setEndProblem] = useState<string | null>(null);

  const openForm = (cover: UserDelegation | null) => {
    setEditing(cover);
    setDialogOpen(true);
  };

  const askToEnd = (cover: UserDelegation) => {
    setEndProblem(null);
    setEnding(cover);
  };

  const onSwitch = (on: boolean) => {
    if (on) openForm(null);
    else if (ooo.current) askToEnd(ooo.current);
  };

  const confirmEnd = async () => {
    if (!ending) return;
    const refused = await ooo.end(ending);
    if (refused) setEndProblem(refused);
    else setEnding(null);
  };

  // First load only: a refresh after a save keeps the page (and any open
  // dialog) where it is.
  if (ooo.loading && !ooo.today) {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-label="Loading out of office">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (ooo.error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-800">Could not load your time away: {ooo.error}</p>
        <Button variant="link" className="mt-2 h-auto p-0 text-red-600" onClick={() => ooo.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const { current, next } = ooo;
  const scheduled = ending?.status === 'scheduled';
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Out of Office</CardTitle>
          <CardDescription>
            Pick a colleague to cover while you are away. Between the dates you set, your
            notifications and approvals go to them instead of you.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!ooo.hasSitePermissions && (
            <p
              className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
              data-testid="ooo-no-site-permissions"
            >
              You have no permissions at {ooo.siteName}, so most of its purchasing and quality
              notifications go to colleagues who do, not to you. While you&apos;re away, your
              cover only receives what would have come to you.
            </p>
          )}
          <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
            <div className="space-y-1">
              <Label htmlFor="ooo-switch" className="text-base">I&apos;m out of office</Label>
              <p className="text-sm text-muted-foreground" data-testid="ooo-summary">
                {current
                  ? `On until ${formatDate(current.end_date)}. ${current.delegate_user_name} is covering for you.`
                  : next
                    ? `Next time away: ${formatDate(next.start_date)} to ${formatDate(next.end_date)}, covered by ${next.delegate_user_name}.`
                    : 'Off. Your notifications and approvals come to you.'}
              </p>
            </div>
            <Switch
              id="ooo-switch"
              checked={current !== null}
              disabled={ooo.saving}
              onCheckedChange={onSwitch}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {current && (
              <Button variant="outline" onClick={() => openForm(current)} disabled={ooo.saving}>
                <Pencil className="mr-2 h-4 w-4" />
                Change dates or colleague
              </Button>
            )}
            <Button variant="outline" onClick={() => openForm(null)} disabled={ooo.saving}>
              <CalendarPlus className="mr-2 h-4 w-4" />
              Plan time away
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your time away</CardTitle>
          <CardDescription>Everything you have planned, including time away that ended or was cancelled.</CardDescription>
        </CardHeader>
        <CardContent>
          {ooo.mine.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              You haven&apos;t planned any time away yet.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Covered by</TableHead>
                  <TableHead>First day</TableHead>
                  <TableHead>Last day</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right"><span className="sr-only">Actions</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ooo.mine.map((cover) => (
                  <TableRow key={cover.id}>
                    <TableCell className="font-medium">{cover.delegate_user_name}</TableCell>
                    <TableCell>{formatDate(cover.start_date)}</TableCell>
                    <TableCell>{formatDate(cover.end_date)}</TableCell>
                    <TableCell className="max-w-[240px] truncate" title={cover.reason}>
                      {cover.reason || '-'}
                    </TableCell>
                    <TableCell><StatusBadge status={cover.status} /></TableCell>
                    <TableCell className="space-x-2 whitespace-nowrap text-right">
                      {(cover.status === 'active' || cover.status === 'scheduled') && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => openForm(cover)} disabled={ooo.saving}>
                            Change
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700"
                            onClick={() => askToEnd(cover)} disabled={ooo.saving}>
                            {cover.status === 'active' ? 'Turn off' : 'Cancel'}
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {ooo.covering.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>You&apos;re covering for</CardTitle>
            <CardDescription>
              While their time away is on, the colleague&apos;s notifications and approvals come to you.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Colleague</TableHead>
                  <TableHead>First day</TableHead>
                  <TableHead>Last day</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ooo.covering.map((cover) => (
                  <TableRow key={cover.id}>
                    <TableCell className="font-medium">{cover.user_name}</TableCell>
                    <TableCell>{formatDate(cover.start_date)}</TableCell>
                    <TableCell>{formatDate(cover.end_date)}</TableCell>
                    <TableCell><StatusBadge status={cover.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <OutOfOfficeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        cover={editing}
        colleagues={ooo.colleagues}
        today={ooo.today}
        saving={ooo.saving}
        onSave={ooo.save}
      />

      <AlertDialog open={ending !== null} onOpenChange={(open) => { if (!open) setEnding(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {scheduled ? 'Cancel this time away?' : 'Turn off out of office?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {scheduled
                ? `${ending?.delegate_user_name} will be told they no longer need to cover.`
                : `Your notifications and approvals come back to you now, and ${ending?.delegate_user_name} will be told.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {endProblem && <p className="text-sm text-red-600" role="alert">{endProblem}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={ooo.saving}>Keep it</AlertDialogCancel>
            {/* A plain Button, not AlertDialogAction: that closes the dialog
                before the answer is back, and a refusal must stay visible. */}
            <Button onClick={confirmEnd} disabled={ooo.saving}>
              {ooo.saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {scheduled ? 'Cancel time away' : 'Turn off'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
